import { decode } from "blurhash";

/**
 * Turns a BlurHash string into a `data:image/png` URI suitable for a CSS
 * `background-image`.
 *
 * Everything here is pure JS (no `<canvas>`, no DOM), so it runs identically in
 * the Node SSR pass (`renderToString`) and in the browser during hydration —
 * the placeholder paints in the server HTML on first byte, and because the
 * output is deterministic there is no hydration mismatch. Results are memoised
 * by hash+size so a grid of cards decodes each unique hash only once.
 */

const cache = new Map<string, string>();
const MAX_CACHE = 256;

export function blurhashToDataUri(hash: string, width = 32, height = 32): string {
  const key = `${hash}:${width}x${height}`;
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  let uri = "";
  try {
    const rgba = decode(hash, width, height); // Uint8ClampedArray, RGBA
    const png = encodePng(new Uint8Array(rgba.buffer), width, height);
    uri = `data:image/png;base64,${base64(png)}`;
  } catch {
    // Invalid hash — fall back to no placeholder rather than throwing in render.
    uri = "";
  }

  if (cache.size >= MAX_CACHE) cache.clear();
  cache.set(key, uri);
  return uri;
}

// --- Minimal, dependency-free PNG encoder (8-bit RGBA, no compression) --------
// Wraps the raw pixels in a single stored (uncompressed) zlib block. Output is
// byte-for-byte deterministic so server and client agree.

function encodePng(rgba: Uint8Array, width: number, height: number): Uint8Array {
  const bytesPerRow = width * 4;
  const raw = new Uint8Array((bytesPerRow + 1) * height);
  for (let y = 0; y < height; y++) {
    raw[y * (bytesPerRow + 1)] = 0; // filter type 0 (none)
    raw.set(rgba.subarray(y * bytesPerRow, (y + 1) * bytesPerRow), y * (bytesPerRow + 1) + 1);
  }

  const ihdr = new Uint8Array(13);
  const dv = new DataView(ihdr.buffer);
  dv.setUint32(0, width);
  dv.setUint32(4, height);
  ihdr[8] = 8; // bit depth
  ihdr[9] = 6; // colour type: RGBA
  // 10..12 already 0: compression, filter, interlace

  const signature = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);
  return concat([signature, chunk("IHDR", ihdr), chunk("IDAT", zlibStore(raw)), chunk("IEND", new Uint8Array(0))]);
}

function chunk(type: string, data: Uint8Array): Uint8Array {
  const typeBytes = Uint8Array.from(type, (c) => c.charCodeAt(0));
  const out = new Uint8Array(12 + data.length);
  const dv = new DataView(out.buffer);
  dv.setUint32(0, data.length);
  out.set(typeBytes, 4);
  out.set(data, 8);
  const crcBody = out.subarray(4, 8 + data.length);
  dv.setUint32(8 + data.length, crc32(crcBody));
  return out;
}

function zlibStore(data: Uint8Array): Uint8Array {
  const blocks: Uint8Array[] = [new Uint8Array([0x78, 0x01])]; // zlib header
  for (let off = 0; off < data.length || off === 0; off += 0xffff) {
    const slice = data.subarray(off, off + 0xffff);
    const final = off + 0xffff >= data.length ? 1 : 0;
    const len = slice.length;
    const header = new Uint8Array(5);
    header[0] = final;
    header[1] = len & 0xff;
    header[2] = (len >> 8) & 0xff;
    header[3] = ~len & 0xff;
    header[4] = (~len >> 8) & 0xff;
    blocks.push(header, slice);
    if (final) break;
  }
  const adler = adler32(data);
  blocks.push(new Uint8Array([(adler >>> 24) & 0xff, (adler >>> 16) & 0xff, (adler >>> 8) & 0xff, adler & 0xff]));
  return concat(blocks);
}

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    const byte = bytes[i] ?? 0;
    c = (CRC_TABLE[(c ^ byte) & 0xff] ?? 0) ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

function adler32(bytes: Uint8Array): number {
  let a = 1;
  let b = 0;
  for (let i = 0; i < bytes.length; i++) {
    a = (a + (bytes[i] ?? 0)) % 65521;
    b = (b + a) % 65521;
  }
  return ((b << 16) | a) >>> 0;
}

function concat(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, p) => sum + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

function base64(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i] ?? 0;
    const b1 = bytes[i + 1] ?? 0;
    const b2 = bytes[i + 2] ?? 0;
    out += B64[b0 >> 2];
    out += B64[((b0 & 3) << 4) | (b1 >> 4)];
    out += i + 1 < bytes.length ? B64[((b1 & 15) << 2) | (b2 >> 6)] : "=";
    out += i + 2 < bytes.length ? B64[b2 & 63] : "=";
  }
  return out;
}
